import { h } from 'preact';
import { NumberFieldEntry, SelectEntry, ListEntry } from '@epx/flow';

const getOverdueTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'Hours', label: i18n['opt.Hours'] || 'Hours' },
  { value: 'Days', label: i18n['opt.Days'] || 'Days' },
  { value: 'Minutes', label: i18n['opt.Minutes'] || 'Minutes' },
];

const getBoundaryEventTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'Timeout', label: i18n['opt.Timeout'] || 'Timeout' },
  { value: 'Error', label: i18n['opt.Error'] || 'Error' },
  { value: 'Escalation', label: i18n['opt.Escalation'] || 'Escalation' },
  { value: 'Cancel', label: i18n['opt.Cancel'] || 'Cancel' },
  { value: 'Compensation', label: i18n['opt.Compensation'] || 'Compensation' },
  { value: 'Signal', label: i18n['opt.Signal'] || 'Signal' },
  { value: 'Message', label: i18n['opt.Message'] || 'Message' },
];

const TASK_TYPES = new Set(['epx:Task', 'epx:UserTask']);

const APPLICABLE_TYPES = new Set([
  'epx:Task',
  'epx:UserTask',
  'epx:BoundaryEvent',
]);

export class SfdBoundaryExtension {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n;
  }

  isApplicable(elementType) {
    return APPLICABLE_TYPES.has(elementType);
  }

  getGroups(element) {
    const lf = this._lf;
    const i18n = this._i18n;
    const props = element.properties || {};
    const id = element.id;
    const isTask = TASK_TYPES.has(element.type);

    const update = (key, value) => {
      lf.setProperties(id, { [key]: value });
      lf.emit('node:properties-change', { id, properties: { [key]: value } });
    };

    const groups = [];

    // Overdue group — for Task and UserTask
    if (isTask) {
      groups.push({
        id: 'sfdBoundary',
        label: i18n.overdue || 'Overdue',
        badge: props.boundaries && props.boundaries.length ? props.boundaries.length : undefined,
        entries: [
          {
            id: 'overdue',
            component: () => (
              <NumberFieldEntry
                id="sfd-overdue"
                label={i18n.overdue || 'Overdue'}
                value={props.overdue || ''}
                min={0}
                step={1}
                description={i18n.overdueHelp || 'Time before task is marked overdue'}
                onChange={(val) => update('overdue', val)}
              />
            ),
          },
          {
            id: 'overdueType',
            component: () => (
              <SelectEntry
                id="sfd-overdueType"
                label={i18n.overdueType || 'Overdue Type'}
                value={props.overdueType || ''}
                options={getOverdueTypes(i18n)}
                onChange={(val) => update('overdueType', val)}
              />
            ),
          },
          {
            id: 'boundariesList',
            component: () => {
              const boundaries = props.boundaries || [];
              return (
                <ListEntry
                  id="sfd-boundaries"
                  label=""
                  items={boundaries}
                  columns={[
                    { key: 'event', label: i18n.boundaryEvent || 'Event', type: 'select', options: getBoundaryEventTypes(i18n), width: '45%' },
                    { key: 'expression', label: i18n.expression || 'Expression', width: '55%' },
                  ]}
                  addLabel={i18n.addBoundary || '+ Add Boundary'}
                  onAdd={() => update('boundaries', [...boundaries, { event: '', expression: '' }])}
                  onRemove={(idx) => update('boundaries', boundaries.filter((_, i) => i !== idx))}
                  onChange={(idx, key, value) => {
                    const updated = boundaries.map((b, i) => (i === idx ? { ...b, [key]: value } : b));
                    update('boundaries', updated);
                  }}
                />
              );
            },
          },
        ],
        isEdited: () => !!(props.overdue || props.overdueType || (props.boundaries && props.boundaries.length)),
      });
    }

    // For BoundaryEvent node itself — just show overdue fields
    if (!isTask) {
      groups.push({
        id: 'sfdBoundary',
        label: i18n.overdue || 'Overdue',
        entries: [
          {
            id: 'overdue',
            component: () => (
              <NumberFieldEntry
                id="sfd-overdue"
                label={i18n.overdue || 'Overdue'}
                value={props.overdue || ''}
                min={0}
                step={1}
                description={i18n.overdueHelp || 'Time before task is marked overdue'}
                onChange={(val) => update('overdue', val)}
              />
            ),
          },
          {
            id: 'overdueType',
            component: () => (
              <SelectEntry
                id="sfd-overdueType"
                label={i18n.overdueType || 'Overdue Type'}
                value={props.overdueType || ''}
                options={getOverdueTypes(i18n)}
                onChange={(val) => update('overdueType', val)}
              />
            ),
          },
        ],
        isEdited: () => !!(props.overdue || props.overdueType),
      });
    }

    return groups;
  }
}
